import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   *
   * Addon rows additionally cap at the main product's current cart quantity
   * minus the sum of OTHER addons' current quantities. This keeps the "+"
   * button disabled the moment an addon reaches its budget, so the user
   * never sees an intermediate value bump (or a negative flicker) when
   * trying to exceed the main quantity.
   *
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, value } = this.getCurrentValues();

    const addonRow = this.closest('[data-addon-product="true"]');
    if (addonRow) {
      const cartItems = addonRow.closest('cart-items-component');

      // Find THIS addon's parent main by Parent Product property linkage.
      // Falls back to the first main in the cart for legacy carts that
      // pre-date the data-parent-product-title attribute.
      const parentTitle = addonRow.dataset.parentProductTitle || '';
      let mainRow = null;
      if (parentTitle) {
        const mains = cartItems?.querySelectorAll('[data-main-product="true"]');
        mains?.forEach((m) => {
          if (!mainRow && (m.dataset.productTitle || '') === parentTitle) {
            mainRow = m;
          }
        });
      }
      if (!mainRow) {
        mainRow = cartItems?.querySelector('[data-main-product="true"]');
      }

      const mainInput = mainRow?.querySelector('input');

      if (mainInput) {
        const mainQty = Math.max(0, parseInt(mainInput.value, 10) || 0);

        // Bundle products multiply the per-VARIANT cap (e.g. a 3-knife set
        // with bundle_item_count=3 allows total variant qty = 3 per main
        // unit). Defaults to 1 for non-bundle products.
        const bundleItemCount = Math.max(
          1,
          parseInt(mainRow.dataset.bundleItemCount ?? '', 10) || 1
        );
        const variantCap = mainQty * bundleItemCount;

        // Aggregate this row's variant across every linked line item under
        // the same parent main. Multiple line items of the SAME variant
        // share a single budget; line items of DIFFERENT variants are
        // independent of one another.
        const variantId = addonRow.dataset.addonVariantId || '';
        const parentTitleForFilter = parentTitle;
        let siblingsTotal = 0;
        if (variantId && cartItems) {
          cartItems
            .querySelectorAll(
              `[data-addon-product="true"][data-addon-variant-id="${variantId}"]`
            )
            .forEach((sib) => {
              if (sib === addonRow) return;
              // Same parent main only (cart can hold multiple bundle mains).
              if (
                parentTitleForFilter &&
                (sib.dataset.parentProductTitle || '') !== parentTitleForFilter
              ) {
                return;
              }
              const inp = sib.querySelector('input');
              siblingsTotal += Math.max(0, parseInt(inp?.value ?? '', 10) || 0);
            });
        }

        // Floor at the current value so we never retroactively forbid an
        // already-accepted quantity (which would also disable the "-" btn).
        const remainingBudget = variantCap - siblingsTotal;
        const addonCap = Math.max(value, remainingBudget);

        return max === null ? addonCap : Math.min(max, addonCap);
      }
    }

    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
